// src/pages/CarDetails.js
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { client } from "../../src/sanity/client"; // Adjust path if needed
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Zoom } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";

function CarDetails() {
  const { slug } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const query = `*[_type == "car" && slug.current == $slug][0]{
      title,
      slug,
      images[]{asset->{url}},
      description,
      price,
      year,
      mileage,
      transmission,
      exteriorColor,
      interiorColor,
      cleanTitle,
      trim,
      isSold
    }`;

    client
      .fetch(query, { slug })
      .then((data) => {
        setCar(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <p className="text-center mt-8 text-lg font-semibold">
        Loading car details...
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-center mt-8 text-red-500 font-semibold">
        Error loading car: {error.message}
      </p>
    );
  }

  if (!car) {
    return (
      <p className="text-center mt-8 text-lg font-semibold">Car not found.</p>
    );
  }


  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl font-open">

      <h1 className="text-4xl font-oswald font-bold mb-6 text-primary">
        {car.title}
      </h1>

      {/* Image Gallery */}
      {car.images && car.images.length > 0 && (
        <div className="mb-8">
          <Swiper
            modules={[Navigation, Pagination, Zoom]}
            navigation
            pagination={{ clickable: true }}
            zoom
            className="rounded-md shadow-md"
          >
            {car.images.map((img, idx) => (
              <SwiperSlide key={idx}>
                <div className="swiper-zoom-container flex justify-center">
                  <img
                    src={img.asset.url}
                    alt={`${car.title} ${idx + 1}`}
                    className="w-full max-w-2xl aspect-[4/3] object-cover rounded-md"
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

       {car.isSold && (
        <p className="text-xl font-bold text-red-600 mb-4">
          This car has been SOLD
        </p>
      )}

      <p className="mb-6 text-lg leading-relaxed">{car.description}</p>

      {/* 2x4 Grid for Car Specs */}


      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <p className="text-md font-semibold text-primary">
          Price:{" "}
          <span className="block font-normal text-md text-gray-800">
            ${car.price}
          </span>
        </p>
        <p className="text-md font-semibold text-primary">
          Year:{" "}
          <span className="block font-normal text-md text-gray-700">
            {car.year || "N/A"}
          </span>
        </p>
        <p className="text-md font-semibold text-primary">
          Mileage:{" "}
          <span className="block font-normal text-md text-gray-700">
            {car.mileage || "N/A"}
          </span>
        </p>
        <p className="text-md font-semibold text-primary">
          Transmission:{" "}
          <span className="block font-normal text-md text-gray-700">
            {car.transmission || "N/A"}
          </span>
        </p>
        <p className="text-md font-semibold text-primary">
          Exterior Color:{" "}
          <span className="block font-normal text-md text-gray-700">
            {car.exteriorColor || "N/A"}
          </span>
        </p>
        <p className="text-md font-semibold text-primary">
          Interior Color:{" "}
          <span className="block font-normal text-md text-gray-700">
            {car.interiorColor || "N/A"}
          </span>
        </p>
        <p className="text-md font-semibold text-primary">
          Clean Title:{" "}
          <span className="block font-normal text-md text-gray-700">
            {car.cleanTitle ? "Yes" : "No"}
          </span>
        </p>
        <p className="text-md font-semibold text-primary">
          Trim:{" "}
          <span className="block font-normal text-md text-gray-700">
            {car.trim || "N/A"}
          </span>
        </p>
      </div>

      {/* Buttons */}
      <div className="flex flex-wrap gap-4 mt-6">
        <Link to="/cars-for-sale">
          <button className="px-6 py-3 rounded-md font-semibold bg-white border border-gray-300 text-gray-800 hover:bg-secondary hover:text-white transition-colors duration-300">
            ← Back to Cars
          </button>
        </Link>

        <Link to={`/contact?car=${encodeURIComponent(car.title)}`}>
          <button className="px-6 py-3 rounded-md font-semibold bg-white border border-gray-300 text-gray-800 hover:bg-secondary hover:text-white transition-colors duration-300">
            Contact Us About This Car
          </button>
        </Link>
      </div>
    </div>
  );
}

export default CarDetails;
